<template>
  <b-modal title=""
           dialog-class="modal-lg"
           :modelValue="invoiceId !== null"
           footer-class="d-flex justify-content-end"
           @update:modelValue="$event === false && (invoiceId = null)">
    <template #default>
      <LoaderWrapper ref="requestsLoader">
        <div class="timeline-horizontal p-3">
          <div v-if="normalizedItems.length" class="timeline-scale mb-2">
            <div class="timeline-scale-labels">
              <div
                v-for="tick in scaleTicks"
                :key="tick.position"
              >
                {{ tick.label }}
              </div>
            </div>
          </div>

          <div
            v-for="item in normalizedItems"
            :key="item.id"
            class="timeline-row"
          >
            <div class="timeline-label">
              {{ item.providerLabel }}
            </div>

            <div class="timeline-track">
              <div
                class="timeline-bar"
                :class="{
            failed: item.status === 'failed',
            pending: item.status === 'pending'
          }"
                :style="{
            left: item.leftPercent + '%',
            width: item.widthPercent + '%'
          }"
              >
          <span>
            {{ item.durationMs != null ? formatDuration(item.durationMs) : 'pending' }}
          </span>
              </div>
            </div>
          </div>

          <div v-if="!normalizedItems.length" class="text-muted small">
            Нет данных по запросам.
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click="invoiceId = null" class="btn btn-outline-secondary btn-sm text-capitalize-first">
        {{ helper.trans('button.ok') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import ProviderRequestDTO from "@/models/ProviderRequestDTO";
import {api} from "@/service/APIClient";

type Status = 'success' | 'failed' | 'pending' | undefined;

interface NormalizedItem {
  id: number;
  providerId: string;
  providerLabel: string;
  status: Status;
  requestDate: Date;
  responseDate: Date;
  durationMs: number;
  leftPercent: number;
  widthPercent: number;
}

interface ScaleTick {
  position: number; // 0..1
  label: string;
}

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper
  },
  setup() {
    const requestsLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      invoiceId: null as string|null,
      requests: [] as ProviderRequestDTO[]
    });

    const show = async (invoiceId: string) => {
      state.invoiceId = invoiceId
      requestsLoader.value?.show()
      try {
        state.requests = await api.call.providers.requestsByInvoice(invoiceId)
      } finally {
        requestsLoader.value?.hide()
      }
    }


    const scaleTicks = computed<ScaleTick[]>(() => {
      if (!normalizedItems.value.length) return [];

      const first = normalizedItems.value[0].requestDate;
      const lastItem = normalizedItems.value[normalizedItems.value.length - 1];
      const lastEnd = lastItem.responseDate ?? lastItem.requestDate;

      let totalMs = lastEnd.getTime() - first.getTime();
      if (totalMs === 0) totalMs = 1;

      const ticks: ScaleTick[] = [];
      const steps = 4

      for (let i = 0; i <= steps; i++) {
        const pos = i / steps;
        const ms = totalMs * pos;
        ticks.push({
          position: pos,
          label: msRangeLabel(ms),
        });
      }

      return ticks;
    });

    const formatDuration = (ms: number): string => {
      if (ms < 1000) return `${ms} ms`;
      const sec = ms / 1000;
      if (sec < 10) return `${sec.toFixed(2)} s`;
      return `${sec.toFixed(1)} s`;
    }

    const msRangeLabel = (ms: number): string  => {
      if (ms < 1000) return `${ms} ms`;
      const sec = ms / 1000;
      if (sec < 10) return `${sec.toFixed(2)} s`;
      return `${sec.toFixed(1)} s`;
    }

    const normalizedItems = computed<NormalizedItem[]>(() => {
      if (!state.requests.length) return [];

      const raw = state.requests.map((i, idx) => {
        const req = new Date(i.requestAt);
        const res = new Date(i.responseAt);

        return {
          id: idx,
          providerId: i.providerId.toString(),
          providerLabel: i.providerSysname,
          status: 'success',
          requestDate: req,
          responseDate: res,
        };
      });

      const minReq = raw.reduce(
        (min, i) => (i.requestDate < min ? i.requestDate : min),
        raw[0].requestDate
      );

      const maxRes = raw.reduce((max, i) => {
        const end = i.responseDate ?? i.requestDate;
        return end > max ? end : max;
      }, raw[0].responseDate ?? raw[0].requestDate);

      let totalMs = maxRes.getTime() - minReq.getTime();
      if (totalMs === 0) totalMs = 1;

      return raw
        .sort((a, b) => b.requestDate.getTime() > a.requestDate.getTime() ? -1 : 1)
        .map((i) => {
          const startOffsetMs = i.requestDate.getTime() - minReq.getTime();
          const durationMs = i.responseDate
            ? i.responseDate.getTime() - i.requestDate.getTime()
            : null;

          const leftPercent = (startOffsetMs / totalMs) * 100;
          const widthPercent = durationMs
            ? Math.max((durationMs / totalMs) * 100, 2)
            : Math.max((totalMs * 0.05) / totalMs * 100, 2);

          return {
            ...i,
            durationMs,
            leftPercent,
            widthPercent,
          } as NormalizedItem;
        });
    });


    return {
      ...toRefs(state),
      show,
      requestsLoader,
      scaleTicks,
      formatDuration,
      normalizedItems,
      helper
    }
  }
})
</script>
